/**
 * Shared Notion-style views for portal content.
 *
 * Exports:
 *   <ViewSwitcher>   — pill-group of Kanban / Gallery / Table / Timeline
 *   <GalleryView>    — card grid
 *   <TableView>      — dense sortable rows
 *   <TimelineView>   — items grouped by month
 *   <LibraryDrawer>  — read-only detail drawer for library items
 *
 * A common item shape is used across every view — see `PortalItem` below.
 *
 * Every view accepts a `columns` prop describing what to render, plus an
 * `onOpen(item)` callback for click / row activation. The parent owns
 * loading, filtering, and (for Content Board) drag-and-drop state.
 */
import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Kanban, LayoutGrid, Table2, CalendarDays, ExternalLink, X, Copy, Check,
  MessageSquarePlus, CheckCircle2, FileText, Image as ImageIcon, Calendar,
} from "lucide-react";
import { voiceColor } from "@/lib/voiceColors";

/**
 * PortalItem — normalized shape every view consumes.
 *
 * @typedef {Object} PortalItem
 * @property {string}  id
 * @property {string=} title
 * @property {string}  preview       — one-paragraph plain-text preview
 * @property {string=} full_text     — full body (for drawer)
 * @property {string=} date          — ISO date
 * @property {string=} voice_id
 * @property {string=} voice_name
 * @property {string=} image_url
 * @property {string=} external_url  — LinkedIn URL if published
 * @property {string=} status        — draft | review | published | archived
 * @property {Object=} metrics       — { likes, comments, reposts }
 * @property {Object=} badges        — { feedback_count, approved, media_type }
 */

const VIEW_META = {
  kanban:   { label: "Board",    Icon: Kanban },
  gallery:  { label: "Gallery",  Icon: LayoutGrid },
  table:    { label: "Table",    Icon: Table2 },
  timeline: { label: "Timeline", Icon: CalendarDays },
};

// ---------------------------------------------------------------------------
// View switcher
// ---------------------------------------------------------------------------
export function ViewSwitcher({ view, onChange, views = ["kanban", "gallery", "table", "timeline"], testid = "view-switcher" }) {
  return (
    <div
      className="inline-flex items-center p-1 rounded-lg bg-white/70 border border-portal-line shadow-sm"
      data-testid={testid}
    >
      {views.map((k) => {
        const m = VIEW_META[k];
        if (!m) return null;
        const active = view === k;
        return (
          <button
            key={k}
            onClick={() => onChange(k)}
            data-testid={`${testid}-${k}`}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium transition-all ${
              active
                ? "bg-portal-accent/10 text-portal-accent shadow-sm"
                : "text-stone-600 hover:text-stone-900 hover:bg-stone-100"
            }`}
          >
            <m.Icon className="w-3.5 h-3.5" strokeWidth={2} />
            <span>{m.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Gallery view
// ---------------------------------------------------------------------------
export function GalleryView({ items, onOpen, showStatus = false, testid = "gallery-view" }) {
  if (items.length === 0) return <EmptyState msg="No posts to show." />;
  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
      data-testid={testid}
    >
      {items.map((it) => (
        <GalleryCard key={it.id} item={it} onOpen={onOpen} showStatus={showStatus} />
      ))}
    </div>
  );
}

function GalleryCard({ item, onOpen, showStatus }) {
  const vc = voiceColor(item.voice_id);
  const status = item.status;
  return (
    <button
      onClick={() => onOpen && onOpen(item)}
      data-testid={`gallery-card-${item.id}`}
      className="text-left group portal-card overflow-hidden hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5 flex flex-col"
      style={{ borderLeft: item.voice_id ? `3px solid ${vc.swatch}` : undefined }}
    >
      {item.image_url ? (
        <div className="aspect-[16/9] bg-portal-line overflow-hidden">
          <img
            src={item.image_url}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        </div>
      ) : (
        <div
          className="aspect-[16/9] flex items-center justify-center"
          style={{ background: `linear-gradient(135deg, ${vc.swatch}0d, ${vc.swatch}18)` }}
        >
          <FileText className="w-8 h-8" style={{ color: `${vc.swatch}66` }} strokeWidth={1.5} />
        </div>
      )}
      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <VoicePill vc={vc} name={item.voice_name} />
          {item.date && (
            <span className="mono text-[10px] text-stone-500">
              {(item.date || "").slice(0, 10)}
            </span>
          )}
          {showStatus && status && <StatusChip status={status} />}
        </div>
        {item.title && (
          <div className="portal-serif text-lg text-stone-900 leading-snug mb-1.5 line-clamp-2">
            {item.title}
          </div>
        )}
        <p className="text-[13px] text-stone-700 leading-relaxed line-clamp-4 whitespace-pre-wrap flex-1">
          {item.preview}
        </p>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-portal-line">
          <div className="flex items-center gap-2">
            {item.badges?.approved && (
              <span className="mono text-[10px] uppercase tracking-wider text-emerald-700 inline-flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> approved
              </span>
            )}
            {item.badges?.feedback_count > 0 && (
              <span className="mono text-[10px] uppercase tracking-wider text-portal-accent inline-flex items-center gap-1">
                <MessageSquarePlus className="w-3 h-3" /> {item.badges.feedback_count}
              </span>
            )}
          </div>
          {item.metrics && (
            <MetricsInline metrics={item.metrics} />
          )}
        </div>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Table view
// ---------------------------------------------------------------------------
export function TableView({ items, onOpen, showStatus = false, showMetrics = true, testid = "table-view" }) {
  if (items.length === 0) return <EmptyState msg="No posts to show." />;
  return (
    <div className="portal-card overflow-hidden" data-testid={testid}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] mono uppercase tracking-wider text-stone-500 bg-white/50">
              <th className="px-6 py-3 font-medium">Post</th>
              <th className="px-3 py-3 font-medium">Voice</th>
              <th className="px-3 py-3 font-medium">Date</th>
              {showStatus && <th className="px-3 py-3 font-medium">Status</th>}
              {showMetrics && (
                <>
                  <th className="px-3 py-3 font-medium text-right">Reactions</th>
                  <th className="px-3 py-3 font-medium text-right">Comments</th>
                  <th className="px-3 py-3 font-medium text-right">Reposts</th>
                </>
              )}
              <th className="px-6 py-3 font-medium text-right">Link</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-portal-line">
            {items.map((it) => (
              <TableRow
                key={it.id}
                item={it}
                onOpen={onOpen}
                showStatus={showStatus}
                showMetrics={showMetrics}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TableRow({ item, onOpen, showStatus, showMetrics }) {
  const vc = voiceColor(item.voice_id);
  const m = item.metrics || {};
  return (
    <tr
      className="hover:bg-portal-cream/50 transition-colors cursor-pointer"
      onClick={() => onOpen && onOpen(item)}
      data-testid={`table-row-${item.id}`}
    >
      <td className="px-6 py-3.5 max-w-[560px]">
        <div className="text-[13.5px] text-stone-900 leading-snug line-clamp-2">
          {item.title || item.preview || "Untitled"}
        </div>
      </td>
      <td className="px-3 py-3.5">
        <VoicePill vc={vc} name={item.voice_name} />
      </td>
      <td className="px-3 py-3.5 mono text-[11px] text-stone-500 whitespace-nowrap">
        {(item.date || "").slice(0, 10) || "—"}
      </td>
      {showStatus && (
        <td className="px-3 py-3.5">
          {item.status ? <StatusChip status={item.status} /> : null}
        </td>
      )}
      {showMetrics && (
        <>
          <td className="px-3 py-3.5 text-right mono tabular-nums text-stone-900">{fmtNum(m.likes)}</td>
          <td className="px-3 py-3.5 text-right mono tabular-nums text-stone-900">{fmtNum(m.comments)}</td>
          <td className="px-3 py-3.5 text-right mono tabular-nums text-stone-900">{fmtNum(m.reposts)}</td>
        </>
      )}
      <td className="px-6 py-3.5 text-right">
        {item.external_url ? (
          <a
            href={item.external_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="mono text-[11px] text-portal-accent hover:underline inline-flex items-center gap-1"
            data-testid={`table-link-${item.id}`}
          >
            open <ExternalLink className="w-3 h-3" />
          </a>
        ) : (
          <span className="mono text-[11px] text-stone-300">—</span>
        )}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Timeline view — items grouped by month, newest first
// ---------------------------------------------------------------------------
export function TimelineView({ items, onOpen, showStatus = false, testid = "timeline-view" }) {
  const groups = useMemo(() => groupByMonth(items), [items]);
  if (items.length === 0) return <EmptyState msg="No posts to show." />;
  return (
    <div className="space-y-8" data-testid={testid}>
      {groups.map((g) => (
        <div key={g.key} data-testid={`timeline-group-${g.key}`}>
          <div className="sticky top-0 z-[1] bg-portal-cream/95 backdrop-blur-sm py-2 mb-3 flex items-baseline gap-3 border-b border-portal-line">
            <h4 className="portal-serif text-xl text-stone-900">{g.label}</h4>
            <span className="mono text-[10px] uppercase tracking-wider text-stone-500">
              {g.items.length} post{g.items.length === 1 ? "" : "s"}
            </span>
          </div>
          <div className="relative pl-6">
            <div className="absolute left-[7px] top-1 bottom-1 w-px bg-portal-line" />
            <div className="space-y-3">
              {g.items.map((it) => (
                <TimelineRow key={it.id} item={it} onOpen={onOpen} showStatus={showStatus} />
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function TimelineRow({ item, onOpen, showStatus }) {
  const vc = voiceColor(item.voice_id);
  return (
    <button
      onClick={() => onOpen && onOpen(item)}
      data-testid={`timeline-row-${item.id}`}
      className="w-full text-left flex items-start gap-4 group relative"
    >
      <div
        className="absolute -left-6 top-2 w-3.5 h-3.5 rounded-full border-2 border-portal-cream"
        style={{ background: vc.swatch }}
      />
      <div className="portal-card flex-1 p-4 hover:shadow-lg transition-shadow">
        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
          <VoicePill vc={vc} name={item.voice_name} />
          {item.date && (
            <span className="mono text-[10px] text-stone-500">
              {(item.date || "").slice(0, 10)}
            </span>
          )}
          {showStatus && item.status && <StatusChip status={item.status} />}
        </div>
        {item.title && (
          <div className="portal-serif text-lg text-stone-900 leading-snug mb-1">{item.title}</div>
        )}
        <p className="text-[13px] text-stone-700 leading-relaxed line-clamp-2 whitespace-pre-wrap">
          {item.preview}
        </p>
        {item.metrics && (
          <div className="mt-2">
            <MetricsInline metrics={item.metrics} />
          </div>
        )}
      </div>
    </button>
  );
}

function groupByMonth(items) {
  const out = new Map();
  for (const it of items) {
    const d = (it.date || "").slice(0, 7) || "unknown";
    if (!out.has(d)) out.set(d, []);
    out.get(d).push(it);
  }
  return [...out.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([key, its]) => ({
      key,
      label: labelForMonth(key),
      items: its,
    }));
}

function labelForMonth(key) {
  if (!key || key === "unknown") return "Undated";
  const [y, m] = key.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

// ---------------------------------------------------------------------------
// Read-only Library detail drawer
// ---------------------------------------------------------------------------
export function LibraryDrawer({ item, onClose }) {
  const [copied, setCopied] = useState(false);
  if (!item) return null;
  const vc = voiceColor(item.voice_id);
  const m = item.metrics || {};

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(item.full_text || item.preview || "");
      setCopied(true);
      toast.success("Caption copied");
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <>
      <div className="portal-drawer-backdrop" onClick={onClose} data-testid="library-drawer-backdrop" />
      <aside className="portal-drawer portal-light" role="dialog" data-testid="library-drawer">
        <div className="sticky top-0 z-10 flex items-center justify-between px-8 py-4 border-b border-portal-line bg-portal-cream/95 backdrop-blur">
          <div className="flex items-center gap-3 flex-wrap">
            <VoicePill vc={vc} name={item.voice_name} />
            {item.date && (
              <span className="mono text-[11px] text-stone-500 inline-flex items-center gap-1">
                <Calendar className="w-3 h-3" /> {item.date.slice(0, 10)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={copy}
              className="portal-btn inline-flex items-center gap-1.5"
              data-testid="library-drawer-copy"
            >
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? "Copied" : "Copy caption"}
            </button>
            {item.external_url && (
              <a
                href={item.external_url}
                target="_blank"
                rel="noopener noreferrer"
                className="portal-btn portal-btn-primary inline-flex items-center gap-1.5"
                data-testid="library-drawer-linkedin"
              >
                Open on LinkedIn <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
            <button
              onClick={onClose}
              className="portal-btn portal-btn-ghost p-2"
              data-testid="library-drawer-close"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="px-8 py-8 max-w-3xl mx-auto space-y-8">
          {item.title && (
            <h2 className="portal-serif text-4xl text-stone-900 leading-tight">{item.title}</h2>
          )}

          {/* Metrics strip */}
          {item.metrics && (
            <div className="portal-card grid grid-cols-3 divide-x divide-portal-line" data-testid="library-drawer-metrics">
              <MetricBlock label="Reactions" value={m.likes} />
              <MetricBlock label="Comments" value={m.comments} />
              <MetricBlock label="Reposts"  value={m.reposts} />
            </div>
          )}

          <article>
            <div className="portal-overline mb-2">Post caption</div>
            <pre
              className="whitespace-pre-wrap break-words font-sans text-[15.5px] leading-[1.8] text-stone-800 m-0"
              data-testid="library-drawer-caption"
            >
{item.full_text || item.preview || ""}
            </pre>
          </article>
        </div>
      </aside>
    </>
  );
}

function MetricBlock({ label, value }) {
  return (
    <div className="px-5 py-4">
      <div className="portal-overline">{label}</div>
      <div className="portal-serif text-3xl text-stone-900 mt-1 tabular-nums">{fmtNum(value)}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small shared bits
// ---------------------------------------------------------------------------
export function VoicePill({ vc, name }) {
  if (!name) return null;
  return (
    <span
      className="text-[10px] mono uppercase tracking-wider px-1.5 py-0.5 rounded inline-flex items-center gap-1"
      style={{ background: `${vc.swatch}14`, color: vc.light }}
    >
      <span
        aria-hidden
        className="inline-block w-1.5 h-1.5 rounded-full"
        style={{ background: vc.swatch }}
      />
      {name}
    </span>
  );
}

export function StatusChip({ status }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.draft;
  return (
    <span
      className="text-[10px] mono uppercase tracking-wider px-1.5 py-0.5 rounded"
      style={{ background: cfg.bg, color: cfg.fg }}
      data-testid={`status-chip-${status}`}
    >
      {cfg.label}
    </span>
  );
}

const STATUS_CFG = {
  draft:     { label: "draft",     bg: "rgba(107,114,128,0.12)", fg: "#4B5563" },
  review:    { label: "in review", bg: "rgba(217,119,6,0.14)",   fg: "#B45309" },
  published: { label: "published", bg: "rgba(14,122,86,0.12)",   fg: "#0E7A56" },
  archived:  { label: "archived",  bg: "rgba(107,114,128,0.08)", fg: "#6B7280" },
};

function MetricsInline({ metrics }) {
  return (
    <div className="flex items-center gap-2.5 text-[11px] mono text-stone-500 tabular-nums">
      <span><b className="text-stone-800">{fmtNum(metrics.likes)}</b>♥</span>
      <span><b className="text-stone-800">{fmtNum(metrics.comments)}</b>💬</span>
      <span><b className="text-stone-800">{fmtNum(metrics.reposts)}</b>↻</span>
    </div>
  );
}

function EmptyState({ msg }) {
  return (
    <div className="portal-card px-10 py-14 text-center text-sm text-stone-500" data-testid="portal-empty">
      {msg}
    </div>
  );
}

function fmtNum(n) {
  if (n === null || n === undefined) return 0;
  if (n >= 1000000) return (n / 1000000).toFixed(1) + "M";
  if (n >= 10000) return (n / 1000).toFixed(1) + "k";
  return n;
}
